/**
 * Performance Mode Detection & Management
 * Automatically detects device capabilities and enables performance optimizations
 * when needed. Uses proactive detection (GPU, memory, system prefs) and reactive
 * monitoring (FPS tracking) for comprehensive coverage.
 */
class PerformanceMode {
  constructor() {
    this.performanceMode = false;
    this.reducedEffects = false;
    this.detectionResults = {};
    this.debugMode = localStorage.getItem('performanceDebugMode') === 'true';
    this.monitoringActive = false;
    this.profileMode = false;
    this.profileSamples = [];

    // Check for URL param modes
    const urlParams = new URLSearchParams(window.location.search);

    // Force performance mode via URL: ?perf_mode=performance|reduced|normal
    const forcedMode = urlParams.get('perf_mode');
    if (forcedMode === 'performance' || forcedMode === '1') {
      this.enable('url-param');
    } else if (forcedMode === 'reduced') {
      this.enableReducedEffects();
      this.detectionResults.enabledBy = 'url-param';
    } else if (forcedMode === 'normal' || forcedMode === '0') {
      // Explicitly disable performance mode and remove any attributes
      this.performanceMode = false;
      this.reducedEffects = false;
      document.documentElement.removeAttribute('data-performance-mode');
      document.documentElement.removeAttribute('data-reduced-effects');
    } else if (urlParams.get('perf_profile') === '1') {
      this.startProfileMode();
    } else if (urlParams.get('perf_diagnose') === '1') {
      // Show diagnostic UI when page loads with ?perf_diagnose=1
      if (document.body) {
        this.showDiagnosticUI();
      } else {
        document.addEventListener('DOMContentLoaded', () => this.showDiagnosticUI());
      }
      this.loadSavedPreference();
    } else {
      // Load saved preference from localStorage (prevents flash on page load)
      this.loadSavedPreference();
    }
  }

  /**
   * Load saved performance preference from localStorage
   * If no preference saved, default to reduced effects and run detection
   */
  loadSavedPreference() {
    const savedMode = localStorage.getItem('performancePreference');
    const lastDetection = localStorage.getItem('performanceLastDetection');
    const daysSinceDetection = lastDetection
      ? (Date.now() - parseInt(lastDetection, 10)) / (1000 * 60 * 60 * 24)
      : Infinity;

    if (savedMode) {
      // Apply saved preference immediately (no flash)
      if (savedMode === 'performance') {
        this.enable('saved-preference');
      } else if (savedMode === 'reduced') {
        this.enableReducedEffects();
        this.detectionResults.enabledBy = 'saved-preference';
      }
      // 'normal' mode = do nothing (effects enabled)

      // Run proactive detection for diagnostics (but don't change mode)
      this.runProactiveDetection(false); // false = don't auto-enable

      // Re-run full detection weekly to catch device changes
      if (daysSinceDetection > 7) {
        this.log('Re-running detection (last run was ' + Math.round(daysSinceDetection) + ' days ago)');
        setTimeout(() => this.runReactiveDetection(), 3000);
      }
    } else {
      // First visit: default to normal mode (full effects)
      // Users can manually enable performance mode if needed
      // Wizard pulse is disabled globally regardless of mode
      this.performanceMode = false;
      this.reducedEffects = false;
      this.savePreference('normal');

      // Run proactive detection for diagnostics only (won't change mode)
      this.runProactiveDetection(false);
    }
  }

  /**
   * Save current performance preference to localStorage
   */
  savePreference(mode) {
    // mode: 'normal', 'reduced', or 'performance'
    localStorage.setItem('performancePreference', mode);
    localStorage.setItem('performanceLastDetection', Date.now().toString());
    this.log('Saved preference:', mode);
  }

  /**
   * Clear saved preference (useful for debug page)
   */
  clearSavedPreference() {
    localStorage.removeItem('performancePreference');
    localStorage.removeItem('performanceLastDetection');
    this.log('Cleared saved preference');
  }

  /**
   * Run a quick reactive detection to validate/upgrade mode
   */
  runReactiveDetection() {
    // Quick 2-second FPS check
    let samples = [];
    let lastTime = performance.now();
    let frameCount = 0;

    const measure = (time) => {
      const delta = time - lastTime;
      if (delta > 0) {
        samples.push(1000 / delta);
      }
      lastTime = time;
      frameCount++;

      if (frameCount < 120) { // ~2 seconds
        requestAnimationFrame(measure);
      } else {
        const avgFPS = samples.reduce((a, b) => a + b) / samples.length;
        const jankFrames = samples.filter(fps => fps < 24).length;
        const jankRatio = jankFrames / samples.length;

        this.log('Reactive detection: avgFPS=' + Math.round(avgFPS) + ', jank=' + Math.round(jankRatio * 100) + '%');

        // Upgrade to performance mode if struggling
        if (avgFPS < 30 || jankRatio > 0.15) {
          if (!this.performanceMode) {
            this.enable('reactive-detection');
            this.savePreference('performance');
          }
        }

        // Save detection timestamp
        localStorage.setItem('performanceLastDetection', Date.now().toString());
      }
    };

    requestAnimationFrame(measure);
  }

  /**
   * PROFILE MODE
   * Triggered by ?perf_profile=1 URL param
   * Shows a floating widget that collects performance data during user interaction
   */
  startProfileMode() {
    this.profileMode = true;
    this.profileSamples = [];

    // Load saved preference first (so profile mode shows current state)
    const savedMode = localStorage.getItem('performancePreference');
    if (savedMode === 'performance') {
      this.enable('saved-preference');
    } else if (savedMode === 'reduced') {
      this.enableReducedEffects();
    } else if (!savedMode) {
      // No saved preference - default to performance mode
      this.enable('default');
    }
    // 'normal' = keep defaults (both false)

    this.detectionResults.proactive = {
      systemPrefs: this.detectSystemPreferences(),
      gpu: this.detectGPU(),
      memory: this.detectMemory(),
      connection: this.detectConnection(),
      timestamp: new Date().toISOString()
    };

    // Create floating profile widget (must wait for body to exist)
    if (document.body) {
      this.createProfileWidget();
      this.startProfileMonitoring();
      this.log('Profile mode started');
    } else {
      // Body doesn't exist yet, wait for DOMContentLoaded
      document.addEventListener('DOMContentLoaded', () => {
        this.createProfileWidget();
        this.startProfileMonitoring();
        this.log('Profile mode started (deferred)');
      });
    }
  }

  /**
   * Create the floating profile widget UI
   */
  createProfileWidget() {
    const widget = document.createElement('div');
    widget.id = 'perf-profile-widget';

    // Determine current mode
    const currentMode = this.performanceMode ? 'performance' : (this.reducedEffects ? 'reduced' : 'normal');

    widget.innerHTML = `
      <div class="perf-profile-header">
        <span class="perf-profile-icon">&#9888;</span>
        Performance Profiling Active
      </div>
      <div class="perf-profile-stats">
        <div class="perf-stat">
          <span class="perf-stat-label">FPS</span>
          <span class="perf-stat-value" id="perf-fps">--</span>
        </div>
        <div class="perf-stat">
          <span class="perf-stat-label">Jank</span>
          <span class="perf-stat-value" id="perf-jank">--</span>
        </div>
        <div class="perf-stat">
          <span class="perf-stat-label">Time</span>
          <span class="perf-stat-value" id="perf-time">0s</span>
        </div>
      </div>
      <div class="perf-mode-toggle">
        <span class="perf-mode-toggle-label">Mode:</span>
        <div class="perf-mode-toggle-btns">
          <button class="perf-mode-toggle-btn ${currentMode === 'normal' ? 'active' : ''}" data-mode="normal" title="Full Effects">&#9733;</button>
          <button class="perf-mode-toggle-btn ${currentMode === 'reduced' ? 'active' : ''}" data-mode="reduced" title="Reduced Effects">&#9681;</button>
          <button class="perf-mode-toggle-btn ${currentMode === 'performance' ? 'active' : ''}" data-mode="performance" title="Performance (Default)">&#9889;</button>
        </div>
      </div>
      <div class="perf-profile-actions">
        <button id="perf-analyze-btn" class="perf-btn perf-btn-primary">Analyze Results</button>
        <button id="perf-stop-btn" class="perf-btn perf-btn-secondary">Stop</button>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.id = 'perf-profile-styles';
    style.textContent = `
      #perf-profile-widget {
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: 280px;
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 1px solid rgba(255, 193, 7, 0.3);
        border-radius: 12px;
        padding: 16px;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
        color: #fff;
      }
      .perf-profile-header {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #ffc107;
      }
      .perf-profile-icon {
        font-size: 18px;
      }
      .perf-profile-stats {
        display: flex;
        gap: 12px;
        margin-bottom: 16px;
      }
      .perf-stat {
        flex: 1;
        text-align: center;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        padding: 8px 4px;
      }
      .perf-stat-label {
        display: block;
        font-size: 10px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 4px;
      }
      .perf-stat-value {
        display: block;
        font-size: 18px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .perf-stat-value.good { color: #4caf50; }
      .perf-stat-value.warn { color: #ffc107; }
      .perf-stat-value.bad { color: #f44336; }
      .perf-profile-actions {
        display: flex;
        gap: 8px;
      }
      .perf-btn {
        flex: 1;
        padding: 10px 12px;
        border: none;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }
      .perf-btn-primary {
        background: #ffc107;
        color: #1a1a2e;
      }
      .perf-btn-primary:hover {
        background: #ffca28;
      }
      .perf-btn-secondary {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
      }
      .perf-btn-secondary:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      /* Mode toggle */
      .perf-mode-toggle {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding: 8px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
      }
      .perf-mode-toggle-label {
        font-size: 11px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
      }
      .perf-mode-toggle-btns {
        display: flex;
        gap: 4px;
        flex: 1;
        justify-content: flex-end;
      }
      .perf-mode-toggle-btn {
        width: 36px;
        height: 36px;
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.05);
        color: rgba(255, 255, 255, 0.6);
        font-size: 16px;
        cursor: pointer;
        transition: all 0.2s;
      }
      .perf-mode-toggle-btn:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.3);
      }
      .perf-mode-toggle-btn.active {
        background: rgba(255, 193, 7, 0.2);
        border-color: #ffc107;
        color: #ffc107;
      }

      /* Results modal */
      #perf-results-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        z-index: 1000000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }
      .perf-results-content {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border-radius: 16px;
        padding: 24px;
        max-width: 500px;
        width: 100%;
        max-height: 80vh;
        overflow-y: auto;
        color: #fff;
      }
      .perf-results-header {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .perf-results-grade {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        font-size: 24px;
        font-weight: 700;
      }
      .perf-results-grade.grade-a { background: #4caf50; }
      .perf-results-grade.grade-b { background: #8bc34a; }
      .perf-results-grade.grade-c { background: #ffc107; color: #1a1a2e; }
      .perf-results-grade.grade-d { background: #ff9800; }
      .perf-results-grade.grade-f { background: #f44336; }
      .perf-results-section {
        margin-bottom: 16px;
        padding: 12px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
      }
      .perf-results-section h4 {
        font-size: 12px;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
        margin: 0 0 8px 0;
      }
      .perf-results-row {
        display: flex;
        justify-content: space-between;
        padding: 4px 0;
        font-size: 14px;
      }
      .perf-results-recommendation {
        padding: 16px;
        background: rgba(255, 193, 7, 0.1);
        border: 1px solid rgba(255, 193, 7, 0.3);
        border-radius: 8px;
        margin-top: 16px;
      }
      .perf-results-recommendation h4 {
        color: #ffc107;
        margin: 0 0 8px 0;
        font-size: 14px;
      }
      .perf-results-recommendation p {
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.8);
      }
      .perf-results-close {
        width: 100%;
        margin-top: 16px;
        padding: 12px;
        background: #ffc107;
        color: #1a1a2e;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
      }
      .perf-results-close:hover {
        background: #ffca28;
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(widget);

    // Bind events
    document.getElementById('perf-analyze-btn').addEventListener('click', () => this.showProfileResults());
    document.getElementById('perf-stop-btn').addEventListener('click', () => this.stopProfileMode());

    // Mode toggle buttons
    widget.querySelectorAll('.perf-mode-toggle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode;
        this.setMode(mode, true); // true = save to localStorage

        // Update active state
        widget.querySelectorAll('.perf-mode-toggle-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
  }

  /**
   * Set performance mode by name
   * @param {string} mode - 'normal', 'reduced', or 'performance'
   * @param {boolean} save - Whether to save to localStorage
   */
  setMode(mode, save = false) {
    // First, disable both modes
    this.performanceMode = false;
    this.reducedEffects = false;
    document.documentElement.removeAttribute('data-performance-mode');
    document.body.removeAttribute('data-performance-mode');
    document.documentElement.removeAttribute('data-reduced-effects');
    document.body.removeAttribute('data-reduced-effects');

    // Then enable the requested mode
    if (mode === 'performance') {
      this.performanceMode = true;
      document.documentElement.setAttribute('data-performance-mode', 'true');
      document.body.setAttribute('data-performance-mode', 'true');
      this.detectionResults.enabledBy = 'manual';
    } else if (mode === 'reduced') {
      this.reducedEffects = true;
      document.documentElement.setAttribute('data-reduced-effects', 'true');
      document.body.setAttribute('data-reduced-effects', 'true');
      this.detectionResults.enabledBy = 'manual';
    }
    // 'normal' = both false, nothing to set

    if (save) {
      this.savePreference(mode);
    }

    this.log('Mode set to:', mode);
  }

  /**
   * Get current mode name
   * @returns {string} 'normal', 'reduced', or 'performance'
   */
  getCurrentMode() {
    if (this.performanceMode) return 'performance';
    if (this.reducedEffects) return 'reduced';
    return 'normal';
  }

  /**
   * Start continuous FPS monitoring for profile mode
   */
  startProfileMonitoring() {
    let lastTime = performance.now();
    let frameCount = 0;
    let currentFPS = 0;
    let jankCount = 0;
    const startTime = Date.now();

    const measure = (time) => {
      if (!this.profileMode) return;

      const delta = time - lastTime;
      if (delta > 0) {
        const fps = 1000 / delta;
        this.profileSamples.push(fps);

        // Track jank (frames > 50ms = dropped frames)
        if (delta > 50) jankCount++;
      }
      lastTime = time;
      frameCount++;

      // Update UI every 30 frames (~500ms)
      if (frameCount % 30 === 0) {
        const recent = this.profileSamples.slice(-30);
        currentFPS = Math.round(recent.reduce((a, b) => a + b) / recent.length);
        const jankRatio = Math.round((jankCount / this.profileSamples.length) * 100);
        const elapsed = Math.round((Date.now() - startTime) / 1000);

        // Update widget
        const fpsEl = document.getElementById('perf-fps');
        const jankEl = document.getElementById('perf-jank');
        const timeEl = document.getElementById('perf-time');

        if (fpsEl) {
          fpsEl.textContent = currentFPS;
          fpsEl.className = 'perf-stat-value ' + (currentFPS >= 50 ? 'good' : currentFPS >= 30 ? 'warn' : 'bad');
        }
        if (jankEl) {
          jankEl.textContent = jankRatio + '%';
          jankEl.className = 'perf-stat-value ' + (jankRatio <= 5 ? 'good' : jankRatio <= 15 ? 'warn' : 'bad');
        }
        if (timeEl) {
          timeEl.textContent = elapsed + 's';
        }
      }

      requestAnimationFrame(measure);
    };

    requestAnimationFrame(measure);
  }

  /**
   * Show profile results in a modal
   */
  showProfileResults() {
    if (this.profileSamples.length < 60) {
      alert('Please interact with the page for at least a few seconds before analyzing.');
      return;
    }

    const samples = this.profileSamples;
    const avgFPS = samples.reduce((a, b) => a + b) / samples.length;
    const minFPS = Math.min(...samples);
    const maxFPS = Math.max(...samples);
    const jankFrames = samples.filter(fps => fps < 20).length;
    const jankRatio = jankFrames / samples.length;

    // Calculate grade
    let grade, gradeClass;
    if (avgFPS >= 55 && jankRatio < 0.02) { grade = 'A'; gradeClass = 'grade-a'; }
    else if (avgFPS >= 45 && jankRatio < 0.05) { grade = 'B'; gradeClass = 'grade-b'; }
    else if (avgFPS >= 30 && jankRatio < 0.10) { grade = 'C'; gradeClass = 'grade-c'; }
    else if (avgFPS >= 20 && jankRatio < 0.20) { grade = 'D'; gradeClass = 'grade-d'; }
    else { grade = 'F'; gradeClass = 'grade-f'; }

    // Build recommendation
    const proactive = this.detectionResults.proactive || {};
    let recommendation = '';
    if (grade === 'A' || grade === 'B') {
      recommendation = 'Your device handles our visual effects smoothly. No changes needed.';
    } else if (grade === 'C') {
      recommendation = 'Some minor performance issues detected. The site should still work well, but you may notice occasional lag during complex animations.';
    } else {
      recommendation = 'We recommend enabling Performance Mode for a smoother experience. This will simplify visual effects while keeping all functionality intact.';
    }

    // Create modal
    const modal = document.createElement('div');
    modal.id = 'perf-results-modal';
    modal.innerHTML = `
      <div class="perf-results-content">
        <div class="perf-results-header">
          <span class="perf-results-grade ${gradeClass}">${grade}</span>
          <span>Performance Analysis</span>
        </div>

        <div class="perf-results-section">
          <h4>Frame Rate</h4>
          <div class="perf-results-row">
            <span>Average FPS</span>
            <span>${Math.round(avgFPS)}</span>
          </div>
          <div class="perf-results-row">
            <span>Minimum FPS</span>
            <span>${Math.round(minFPS)}</span>
          </div>
          <div class="perf-results-row">
            <span>Maximum FPS</span>
            <span>${Math.round(maxFPS)}</span>
          </div>
          <div class="perf-results-row">
            <span>Dropped Frames</span>
            <span>${Math.round(jankRatio * 100)}%</span>
          </div>
        </div>

        <div class="perf-results-section">
          <h4>Device Info</h4>
          <div class="perf-results-row">
            <span>GPU</span>
            <span>${proactive.gpu?.renderer?.substring(0, 30) || 'Unknown'}${proactive.gpu?.renderer?.length > 30 ? '...' : ''}</span>
          </div>
          <div class="perf-results-row">
            <span>Memory</span>
            <span>${proactive.memory?.memory ? proactive.memory.memory + ' GB' : 'Unknown'}</span>
          </div>
          <div class="perf-results-row">
            <span>CPU Cores</span>
            <span>${proactive.memory?.cores || 'Unknown'}</span>
          </div>
        </div>

        <div class="perf-results-section">
          <h4>Test Duration</h4>
          <div class="perf-results-row">
            <span>Frames Analyzed</span>
            <span>${samples.length}</span>
          </div>
          <div class="perf-results-row">
            <span>Duration</span>
            <span>${Math.round(samples.length / 60)}s</span>
          </div>
        </div>

        <div class="perf-results-recommendation">
          <h4>Recommendation</h4>
          <p>${recommendation}</p>
        </div>

        ${grade === 'D' || grade === 'F' ? `
        <button onclick="window.performanceMode.enable('user-profile'); this.closest('#perf-results-modal').remove(); document.getElementById('perf-profile-widget')?.remove();" class="perf-results-close" style="background: #f44336; margin-bottom: 8px;">
          Enable Performance Mode
        </button>
        ` : ''}

        <button onclick="this.closest('#perf-results-modal').remove()" class="perf-results-close">
          Close
        </button>
      </div>
    `;

    document.body.appendChild(modal);

    // Store results
    this.detectionResults.profile = {
      avgFPS: Math.round(avgFPS * 10) / 10,
      minFPS: Math.round(minFPS * 10) / 10,
      maxFPS: Math.round(maxFPS * 10) / 10,
      jankRatio: Math.round(jankRatio * 1000) / 1000,
      grade,
      samples: samples.length,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Stop profile mode
   */
  stopProfileMode() {
    this.profileMode = false;
    document.getElementById('perf-profile-widget')?.remove();
    document.getElementById('perf-profile-styles')?.remove();

    // Remove URL param
    const url = new URL(window.location.href);
    url.searchParams.delete('perf_profile');
    window.history.replaceState({}, '', url);
  }

  /**
   * PROACTIVE DETECTION
   * Run at page load to detect device capabilities
   * @param {boolean} autoEnable - Whether to auto-enable performance mode if issues detected
   */
  runProactiveDetection(autoEnable = true) {
    const signals = {
      systemPrefs: this.detectSystemPreferences(),
      gpu: this.detectGPU(),
      memory: this.detectMemory(),
      connection: this.detectConnection(),
      timestamp: new Date().toISOString()
    };

    this.detectionResults.proactive = signals;

    // Decision logic - enable if any red flags
    const shouldEnablePerformance =
      signals.systemPrefs.reducedMotion ||
      signals.systemPrefs.reducedTransparency ||
      signals.gpu.isSoftwareRenderer ||
      !signals.gpu.supported ||
      signals.memory.tier === 'low' ||
      signals.connection.saveData;

    // Only upgrade to performance mode if not already enabled
    // We no longer downgrade capable devices to "reduced" - performance mode is the default
    if (autoEnable && shouldEnablePerformance && !this.performanceMode) {
      this.enable('proactive');
      this.savePreference('performance');
    }

    this.log('Proactive detection complete', signals);
  }

  /**
   * Detect system accessibility preferences
   */
  detectSystemPreferences() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reducedTransparency = window.matchMedia('(prefers-reduced-transparency: reduce)').matches;

    // Listen for changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      if (e.matches) this.enable('system-preference-change');
    });

    return {
      reducedMotion,
      reducedTransparency
    };
  }

  /**
   * Detect GPU capabilities via WebGL
   */
  detectGPU() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) {
        return { supported: false, tier: 'low', renderer: 'none' };
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'unknown';
      const vendor = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'unknown';

      // Detect software rendering (very slow)
      const isSoftwareRenderer = /SwiftShader|llvmpipe|Mesa|Software|Microsoft Basic|ANGLE.*Direct3D9/i.test(renderer);

      // Detect integrated GPUs (moderate performance)
      const isIntegrated = /Intel|HD Graphics|UHD Graphics|Iris|Mali|Adreno|Apple M\d|PowerVR/i.test(renderer);

      // Determine tier
      let tier = 'high';
      if (isSoftwareRenderer) tier = 'low';
      else if (isIntegrated) tier = 'medium';

      return {
        supported: true,
        renderer,
        vendor,
        isSoftwareRenderer,
        isIntegrated,
        tier
      };
    } catch (e) {
      return {
        supported: false,
        tier: 'low',
        error: e.message
      };
    }
  }

  /**
   * Detect device memory and CPU cores
   */
  detectMemory() {
    // navigator.deviceMemory returns RAM in GB (Chrome/Edge only)
    // Values are rounded: 0.25, 0.5, 1, 2, 4, 8
    const memory = navigator.deviceMemory || null;

    // navigator.hardwareConcurrency returns logical CPU cores
    const cores = navigator.hardwareConcurrency || null;

    // Determine tier based on memory and cores
    let tier = 'high';
    if (memory !== null && memory <= 2) tier = 'low';
    else if (cores !== null && cores <= 2) tier = 'low';
    else if ((memory !== null && memory <= 4) || (cores !== null && cores <= 4)) tier = 'medium';

    return {
      memory,
      cores,
      tier
    };
  }

  /**
   * Detect network connection info
   */
  detectConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (!conn) {
      return {
        available: false,
        saveData: false,
        effectiveType: 'unknown'
      };
    }

    return {
      available: true,
      saveData: conn.saveData || false,
      effectiveType: conn.effectiveType || 'unknown',
      downlink: conn.downlink || null,
      rtt: conn.rtt || null
    };
  }

  /**
   * REACTIVE MONITORING
   * Continuously monitor FPS during use
   */
  startReactiveMonitoring() {
    // Skip if already in performance mode or monitoring
    if (this.performanceMode || this.monitoringActive) return;

    this.monitoringActive = true;
    let samples = [];
    let lastTime = performance.now();
    let analysisCount = 0;
    const MAX_ANALYSES = 10; // Stop after 10 analysis windows (20 seconds)

    const measure = (time) => {
      // Stop if performance mode enabled or max analyses reached
      if (this.performanceMode || analysisCount >= MAX_ANALYSES) {
        this.monitoringActive = false;
        return;
      }

      const delta = time - lastTime;
      if (delta > 0) {
        samples.push(1000 / delta);
      }
      lastTime = time;

      // Analyze every ~2 seconds (120 frames at 60fps)
      if (samples.length >= 120) {
        const avgFPS = samples.reduce((a, b) => a + b) / samples.length;
        const jankFrames = samples.filter(fps => fps < 24).length;
        const jankRatio = jankFrames / samples.length;

        this.detectionResults.reactive = {
          avgFPS: Math.round(avgFPS * 10) / 10,
          jankRatio: Math.round(jankRatio * 1000) / 1000,
          sampleCount: samples.length,
          analysisNumber: analysisCount + 1,
          timestamp: new Date().toISOString()
        };

        this.log('Reactive analysis', this.detectionResults.reactive);

        // Enable if: avg FPS < 30 OR > 15% janky frames
        if (avgFPS < 30 || jankRatio > 0.15) {
          this.enable('reactive');
          this.savePreference('performance');
          this.monitoringActive = false;
          return;
        }

        samples = [];
        analysisCount++;
      }

      requestAnimationFrame(measure);
    };

    requestAnimationFrame(measure);
  }

  /**
   * Enable performance mode
   * @param {string} source - What triggered the enable
   * @param {boolean} save - Whether to save to localStorage (default: true for manual)
   */
  enable(source = 'manual', save = null) {
    if (this.performanceMode) return; // Already enabled

    this.performanceMode = true;
    this.detectionResults.enabledBy = source;
    this.detectionResults.enabledAt = new Date().toISOString();

    document.documentElement.setAttribute('data-performance-mode', 'true');
    if (document.body) {
      document.body.setAttribute('data-performance-mode', 'true');
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.setAttribute('data-performance-mode', 'true');
      });
    }

    // Disable will-change on elements (can cause memory issues)
    document.querySelectorAll('[style*="will-change"]').forEach(el => {
      el.style.willChange = 'auto';
    });

    // Save preference if manual toggle or explicitly requested
    if (save === true || (save === null && source === 'manual')) {
      this.savePreference('performance');
    }

    this.log('Performance mode ENABLED by:', source);
  }

  /**
   * Disable performance mode (for debug page)
   * @param {boolean} save - Whether to save to localStorage
   */
  disable(save = false) {
    this.performanceMode = false;
    delete this.detectionResults.enabledBy;
    delete this.detectionResults.enabledAt;

    document.documentElement.removeAttribute('data-performance-mode');
    document.body.removeAttribute('data-performance-mode');

    // If also disabling reduced effects, save as 'normal'
    // Otherwise save as 'reduced' (the default moderate mode)
    if (save) {
      this.savePreference(this.reducedEffects ? 'reduced' : 'normal');
    }

    this.log('Performance mode DISABLED');
  }

  /**
   * Toggle performance mode (for debug page)
   */
  toggle() {
    if (this.performanceMode) {
      this.disable();
    } else {
      this.enable('manual');
    }
  }

  /**
   * Enable reduced effects mode (lighter than full performance mode)
   */
  enableReducedEffects() {
    this.reducedEffects = true;
    document.documentElement.setAttribute('data-reduced-effects', 'true');
    if (document.body) {
      document.body.setAttribute('data-reduced-effects', 'true');
    } else {
      // Body doesn't exist yet, wait for it
      document.addEventListener('DOMContentLoaded', () => {
        document.body.setAttribute('data-reduced-effects', 'true');
      });
    }
  }

  /**
   * Disable reduced effects mode
   */
  disableReducedEffects() {
    this.reducedEffects = false;
    document.documentElement.removeAttribute('data-reduced-effects');
    document.body.removeAttribute('data-reduced-effects');
  }

  /**
   * Get debug info (for admin debug page)
   */
  getDebugInfo() {
    return {
      enabled: this.performanceMode,
      reducedEffects: this.reducedEffects,
      results: this.detectionResults,
      userAgent: navigator.userAgent,
      screenSize: {
        width: window.screen.width,
        height: window.screen.height,
        devicePixelRatio: window.devicePixelRatio
      }
    };
  }

  /**
   * Run a stress test (for admin debug page)
   * Creates multiple animated elements and measures FPS
   */
  runStressTest(callback) {
    const container = document.createElement('div');
    container.id = 'performance-stress-test';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 999999;
      overflow: hidden;
    `;

    // Create 50 animated boxes
    for (let i = 0; i < 50; i++) {
      const box = document.createElement('div');
      box.style.cssText = `
        position: absolute;
        width: 100px;
        height: 100px;
        background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
        backdrop-filter: blur(10px);
        border-radius: 10px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        animation: stressMove${i % 3} ${2 + Math.random() * 2}s infinite ease-in-out;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
      `;
      container.appendChild(box);
    }

    // Add animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes stressMove0 {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(100px, 100px) rotate(180deg) scale(1.2); }
      }
      @keyframes stressMove1 {
        0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        50% { transform: translate(-100px, 50px) rotate(-180deg); opacity: 0.5; }
      }
      @keyframes stressMove2 {
        0%, 100% { transform: scale(1) translateY(0); }
        50% { transform: scale(1.3) translateY(-50px); }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(container);

    // Measure FPS during stress test
    let samples = [];
    let lastTime = performance.now();
    let frameCount = 0;

    const measure = (time) => {
      const delta = time - lastTime;
      if (delta > 0) {
        samples.push(1000 / delta);
      }
      lastTime = time;
      frameCount++;

      if (frameCount < 180) { // 3 seconds
        requestAnimationFrame(measure);
      } else {
        // Cleanup
        container.remove();
        style.remove();

        // Calculate results
        const avgFPS = samples.reduce((a, b) => a + b) / samples.length;
        const minFPS = Math.min(...samples);
        const maxFPS = Math.max(...samples);
        const jankFrames = samples.filter(fps => fps < 24).length;

        const results = {
          avgFPS: Math.round(avgFPS * 10) / 10,
          minFPS: Math.round(minFPS * 10) / 10,
          maxFPS: Math.round(maxFPS * 10) / 10,
          jankRatio: Math.round((jankFrames / samples.length) * 1000) / 1000,
          frames: samples.length,
          recommendation: avgFPS < 30 ? 'Enable performance mode' : 'Device handles effects well'
        };

        this.detectionResults.stressTest = results;
        this.log('Stress test complete', results);

        if (callback) callback(results);
      }
    };

    requestAnimationFrame(measure);
  }

  /**
   * Export debug info as JSON (for admin debug page)
   */
  exportDebugInfo() {
    const info = this.getDebugInfo();
    const blob = new Blob([JSON.stringify(info, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-debug-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Generate a comprehensive diagnostic report for end users
   * This collects all performance data and makes it easy to share
   */
  generateDiagnosticReport() {
    const report = {
      // Timestamp and page info
      timestamp: new Date().toISOString(),
      url: window.location.href,
      pageTitle: document.title,

      // Device & Browser
      device: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
        deviceMemory: navigator.deviceMemory || 'unknown',
        maxTouchPoints: navigator.maxTouchPoints || 0,
      },

      // Screen info
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        orientation: window.screen.orientation?.type || 'unknown',
      },

      // Viewport
      viewport: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      },

      // GPU detection
      gpu: this.detectGPU(),

      // Memory (if available)
      memory: this.detectMemory(),

      // Connection
      connection: this.detectConnection(),

      // System preferences
      systemPrefs: this.detectSystemPreferences(),

      // Performance mode state
      performanceState: {
        performanceMode: this.performanceMode,
        reducedEffects: this.reducedEffects,
        enabledBy: this.detectionResults.enabledBy,
        savedPreference: localStorage.getItem('performancePreference'),
      },

      // DOM stats
      domStats: {
        totalElements: document.querySelectorAll('*').length,
        totalImages: document.images.length,
        totalScripts: document.scripts.length,
        totalStylesheets: document.styleSheets.length,
        totalForms: document.forms.length,
        totalInputs: document.querySelectorAll('input, select, textarea').length,
        bodyClasses: document.body.className,
        htmlClasses: document.documentElement.className,
      },

      // Animation stats
      animations: {
        activeAnimations: document.getAnimations?.()?.length || 'unsupported',
        elementsWithWillChange: this._countElementsWithStyle('willChange', v => v !== 'auto'),
        elementsWithTransform: this._countElementsWithStyle('transform', v => v !== 'none'),
        elementsWithBackdropFilter: this._countElementsWithStyle('backdropFilter', v => v !== 'none'),
        elementsWithFilter: this._countElementsWithStyle('filter', v => v !== 'none'),
      },

      // Performance timing (if available)
      timing: this._getPerformanceTiming(),

      // Profile mode results (if ran)
      profileResults: this.profileStats ? {
        samples: this.profileStats.samples?.length || 0,
        avgFPS: this._calculateAvgFPS(),
        jankPercentage: this._calculateJankPercentage(),
      } : null,

      // Detection results
      detectionResults: this.detectionResults,

      // Console errors (last 10)
      recentErrors: window._perfDiagErrors || [],
    };

    return report;
  }

  /**
   * Helper: count elements matching a computed style condition
   */
  _countElementsWithStyle(prop, condition) {
    let count = 0;
    try {
      document.querySelectorAll('*').forEach(el => {
        const style = getComputedStyle(el);
        if (condition(style[prop])) count++;
      });
    } catch (e) {
      return 'error';
    }
    return count;
  }

  /**
   * Helper: get performance timing data
   */
  _getPerformanceTiming() {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav) {
        return {
          domContentLoaded: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
          domComplete: Math.round(nav.domComplete - nav.startTime),
          loadComplete: Math.round(nav.loadEventEnd - nav.startTime),
          ttfb: Math.round(nav.responseStart - nav.requestStart),
        };
      }
    } catch (e) {}
    return null;
  }

  /**
   * Helper: calculate average FPS from profile stats
   */
  _calculateAvgFPS() {
    if (!this.profileStats?.samples?.length) return null;
    const samples = this.profileStats.samples;
    return Math.round(samples.reduce((a, b) => a + b, 0) / samples.length);
  }

  /**
   * Helper: calculate jank percentage from profile stats
   */
  _calculateJankPercentage() {
    if (!this.profileStats?.samples?.length) return null;
    const samples = this.profileStats.samples;
    const jankFrames = samples.filter(fps => fps < 30).length;
    return Math.round((jankFrames / samples.length) * 100);
  }

  /**
   * Show diagnostic UI for end users
   * Call via: window.performanceMode.showDiagnosticUI()
   * Or visit page with ?perf_diagnose=1
   */
  showDiagnosticUI() {
    // Remove existing
    document.getElementById('perf-diagnostic-ui')?.remove();
    document.getElementById('perf-diagnostic-overlay')?.remove();

    const container = document.createElement('div');
    container.id = 'perf-diagnostic-ui';

    // Create overlay separately
    const overlay = document.createElement('div');
    overlay.id = 'perf-diagnostic-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.7);
      z-index: 999998;
    `;
    document.body.appendChild(overlay);

    container.innerHTML = `
      <style>
        #perf-diagnostic-ui {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #1a1a2e;
          border: 2px solid #4a9eff;
          border-radius: 12px;
          padding: 24px;
          z-index: 999999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #fff;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }
        #perf-diagnostic-ui h2 {
          margin: 0 0 16px 0;
          font-size: 18px;
          color: #4a9eff;
        }
        #perf-diagnostic-ui p {
          margin: 0 0 16px 0;
          font-size: 14px;
          line-height: 1.5;
          color: #ccc;
        }
        #perf-diagnostic-ui .steps {
          background: rgba(255,255,255,0.05);
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }
        #perf-diagnostic-ui .steps ol {
          margin: 0;
          padding-left: 20px;
        }
        #perf-diagnostic-ui .steps li {
          margin-bottom: 8px;
          font-size: 14px;
        }
        #perf-diagnostic-ui .btn-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        #perf-diagnostic-ui button {
          padding: 12px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        #perf-diagnostic-ui .btn-primary {
          background: #4a9eff;
          color: #fff;
        }
        #perf-diagnostic-ui .btn-primary:hover {
          background: #3a8eef;
        }
        #perf-diagnostic-ui .btn-secondary {
          background: #333;
          color: #fff;
        }
        #perf-diagnostic-ui .btn-secondary:hover {
          background: #444;
        }
        #perf-diagnostic-ui .btn-success {
          background: #22c55e;
          color: #fff;
        }
        #perf-diagnostic-ui .status {
          margin-top: 16px;
          padding: 12px;
          border-radius: 6px;
          font-size: 13px;
          display: none;
        }
        #perf-diagnostic-ui .status.show {
          display: block;
        }
        #perf-diagnostic-ui .status.success {
          background: rgba(34, 197, 94, 0.2);
          border: 1px solid #22c55e;
        }
        #perf-diagnostic-ui .status.info {
          background: rgba(74, 158, 255, 0.2);
          border: 1px solid #4a9eff;
        }
        #perf-diagnostic-ui .close-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          color: #666;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }
        #perf-diagnostic-ui .close-btn:hover {
          color: #fff;
        }
      </style>
      <button class="close-btn">&times;</button>
      <h2>Performance Diagnostic Tool</h2>
      <p>Help us debug performance issues by generating a diagnostic report.</p>

      <div class="steps">
        <ol>
          <li>Click <strong>"Run 5-Second Test"</strong> and wait</li>
          <li>Scroll around and interact with the page during the test</li>
          <li>Click <strong>"Copy Report"</strong> when done</li>
          <li>Paste in an email to support</li>
        </ol>
      </div>

      <div class="btn-row">
        <button class="btn-primary" id="perf-diag-run">Run 5-Second Test</button>
        <button class="btn-secondary" id="perf-diag-copy">Copy Report</button>
        <button class="btn-secondary" id="perf-diag-download">Download JSON</button>
      </div>

      <div class="status" id="perf-diag-status"></div>
    `;

    document.body.appendChild(container);

    // Bind events
    document.getElementById('perf-diag-run').addEventListener('click', () => {
      const status = document.getElementById('perf-diag-status');
      const btn = document.getElementById('perf-diag-run');

      btn.disabled = true;
      btn.textContent = 'Testing... (5s)';
      status.className = 'status show info';
      status.textContent = 'Running performance test. Please scroll and interact with the page...';

      // Start FPS monitoring for 5 seconds
      this.profileStats = { samples: [], startTime: performance.now() };
      let lastTime = performance.now();

      const measure = (time) => {
        const delta = time - lastTime;
        if (delta > 0) {
          this.profileStats.samples.push(1000 / delta);
        }
        lastTime = time;

        if (performance.now() - this.profileStats.startTime < 5000) {
          requestAnimationFrame(measure);
        } else {
          btn.disabled = false;
          btn.textContent = 'Run 5-Second Test';
          status.className = 'status show success';
          status.textContent = `Test complete! Avg FPS: ${this._calculateAvgFPS()}, Jank: ${this._calculateJankPercentage()}%. Click "Copy Report" to share.`;
        }
      };

      requestAnimationFrame(measure);
    });

    document.getElementById('perf-diag-copy').addEventListener('click', () => {
      const report = this.generateDiagnosticReport();
      const text = this._formatReportAsText(report);

      navigator.clipboard.writeText(text).then(() => {
        const status = document.getElementById('perf-diag-status');
        status.className = 'status show success';
        status.textContent = 'Report copied to clipboard! Paste it in your email to support.';
      }).catch(() => {
        // Fallback
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();

        const status = document.getElementById('perf-diag-status');
        status.className = 'status show success';
        status.textContent = 'Report copied! Paste it in your email to support.';
      });
    });

    document.getElementById('perf-diag-download').addEventListener('click', () => {
      const report = this.generateDiagnosticReport();
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perf-diagnostic-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);

      const status = document.getElementById('perf-diag-status');
      status.className = 'status show success';
      status.textContent = 'JSON file downloaded!';
    });

    // Close button
    container.querySelector('.close-btn').addEventListener('click', () => {
      container.remove();
      overlay.remove();
    });

    // Close on overlay click
    overlay.addEventListener('click', () => {
      container.remove();
      overlay.remove();
    });
  }

  /**
   * Format report as human-readable text for email
   */
  _formatReportAsText(report) {
    return `
=== PERFORMANCE DIAGNOSTIC REPORT ===
Generated: ${report.timestamp}
Page: ${report.url}

--- DEVICE ---
Browser: ${report.device.userAgent}
Platform: ${report.device.platform}
CPU Cores: ${report.device.hardwareConcurrency}
RAM: ${report.device.deviceMemory ? report.device.deviceMemory + ' GB' : 'unknown'}
Touch: ${report.device.maxTouchPoints > 0 ? 'Yes' : 'No'}

--- SCREEN ---
Resolution: ${report.screen.width}x${report.screen.height}
Viewport: ${report.viewport.innerWidth}x${report.viewport.innerHeight}
Pixel Ratio: ${report.screen.pixelRatio}

--- GPU ---
Renderer: ${report.gpu.renderer || 'unknown'}
Vendor: ${report.gpu.vendor || 'unknown'}
Software Renderer: ${report.gpu.isSoftwareRenderer ? 'YES (slow!)' : 'No'}

--- PERFORMANCE TEST ---
${report.profileResults ? `
Average FPS: ${report.profileResults.avgFPS}
Jank %: ${report.profileResults.jankPercentage}%
Samples: ${report.profileResults.samples}
` : 'No test run yet - click "Run 5-Second Test"'}

--- PAGE STATS ---
DOM Elements: ${report.domStats.totalElements}
Images: ${report.domStats.totalImages}
Scripts: ${report.domStats.totalScripts}
Inputs: ${report.domStats.totalInputs}

--- ANIMATIONS ---
Active Animations: ${report.animations.activeAnimations}
GPU Layers (will-change): ${report.animations.elementsWithWillChange}
Transforms: ${report.animations.elementsWithTransform}
Backdrop Filters: ${report.animations.elementsWithBackdropFilter}
Filters: ${report.animations.elementsWithFilter}

--- LOAD TIME ---
${report.timing ? `
DOM Ready: ${report.timing.domContentLoaded}ms
Page Load: ${report.timing.loadComplete}ms
TTFB: ${report.timing.ttfb}ms
` : 'Not available'}

--- PERFORMANCE MODE ---
Enabled: ${report.performanceState.performanceMode ? 'Yes' : 'No'}
Reduced Effects: ${report.performanceState.reducedEffects ? 'Yes' : 'No'}
Enabled By: ${report.performanceState.enabledBy || 'not enabled'}

--- SYSTEM PREFS ---
Prefers Reduced Motion: ${report.systemPrefs.reducedMotion ? 'Yes' : 'No'}
Prefers Reduced Transparency: ${report.systemPrefs.reducedTransparency ? 'Yes' : 'No'}

=== END REPORT ===
`.trim();
  }

  /**
   * Enable debug mode (logs to console)
   */
  enableDebugMode() {
    this.debugMode = true;
    localStorage.setItem('performanceDebugMode', 'true');
    console.log('[PerformanceMode] Debug mode enabled. Current state:', this.getDebugInfo());
  }

  /**
   * Disable debug mode
   */
  disableDebugMode() {
    this.debugMode = false;
    localStorage.removeItem('performanceDebugMode');
  }

  /**
   * Internal logging (only when debug mode is on)
   */
  log(...args) {
    if (this.debugMode) {
      console.log('[PerformanceMode]', ...args);
    }
  }

  // ========================================
  // Effect-by-Effect Benchmark System
  // ========================================

  /**
   * Run effect-by-effect benchmark
   * Tests each visual effect individually to identify performance impact
   * @param {Function} progressCallback - Called with progress updates
   * @param {Function} completeCallback - Called with final results
   */
  async runEffectBenchmark(progressCallback, completeCallback) {
    const effects = [
      {
        id: 'baseline',
        name: 'Baseline (No Effects)',
        description: 'Clean elements with no effects',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
        `
      },
      {
        id: 'backdrop-blur',
        name: 'Backdrop Blur',
        description: 'backdrop-filter: blur(20px)',
        styles: `
          background: rgba(42, 42, 64, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 10px;
        `
      },
      {
        id: 'shadow-complex',
        name: 'Complex Shadows',
        description: 'Multi-layer box-shadow with glow',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          box-shadow:
            0 4px 20px rgba(0,0,0,0.4),
            0 0 30px rgba(146, 64, 14, 0.15),
            inset 0 1px 0 rgba(255,255,255,0.1);
        `
      },
      {
        id: 'shadow-simple',
        name: 'Simple Shadow',
        description: 'Single-layer box-shadow',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        `
      },
      {
        id: 'animation-pulse',
        name: 'Pulse Animation',
        description: 'Continuous scale/opacity animation',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          animation: effectTestPulse 1.5s ease-in-out infinite;
        `
      },
      {
        id: 'animation-glow',
        name: 'Glow Animation',
        description: 'Animated shadow glow',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          animation: effectTestGlow 2s ease-in-out infinite;
        `
      },
      {
        id: 'transitions',
        name: 'Hover Transitions',
        description: 'All properties transition on hover',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          transition: all 0.3s ease;
        `,
        hoverStyles: `
          transform: scale(1.05);
          box-shadow: 0 8px 25px rgba(0,0,0,0.3);
        `
      },
      {
        id: 'gradient-animated',
        name: 'Animated Gradient',
        description: 'Moving gradient background',
        styles: `
          background: linear-gradient(45deg, #2a2a40, #4a3a60, #2a2a40);
          background-size: 200% 200%;
          border-radius: 10px;
          animation: effectTestGradient 3s ease infinite;
        `
      },
      {
        id: 'combined-light',
        name: 'Combined (Light)',
        description: 'Simple shadow + transition',
        styles: `
          background: #2a2a40;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          transition: transform 0.2s ease;
        `
      },
      {
        id: 'combined-full',
        name: 'Combined (Full)',
        description: 'Blur + complex shadow + animation',
        styles: `
          background: rgba(42, 42, 64, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-radius: 10px;
          box-shadow:
            0 4px 20px rgba(0,0,0,0.4),
            0 0 20px rgba(146, 64, 14, 0.1);
          animation: effectTestPulse 2s ease-in-out infinite;
        `
      }
    ];

    const results = [];
    let baselineFPS = 60;

    // Add test keyframes
    const keyframeStyle = document.createElement('style');
    keyframeStyle.id = 'effect-benchmark-keyframes';
    keyframeStyle.textContent = `
      @keyframes effectTestPulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.05); opacity: 0.9; }
      }
      @keyframes effectTestGlow {
        0%, 100% { box-shadow: 0 0 10px rgba(146, 64, 14, 0.3); }
        50% { box-shadow: 0 0 30px rgba(146, 64, 14, 0.6); }
      }
      @keyframes effectTestGradient {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes effectTestMove {
        0%, 100% { transform: translate(0, 0); }
        50% { transform: translate(20px, 20px); }
      }
    `;
    document.head.appendChild(keyframeStyle);

    for (let i = 0; i < effects.length; i++) {
      const effect = effects[i];

      if (progressCallback) {
        progressCallback({
          current: i + 1,
          total: effects.length,
          effect: effect.name,
          phase: 'testing'
        });
      }

      const result = await this.testSingleEffect(effect);

      if (effect.id === 'baseline') {
        baselineFPS = result.avgFPS;
      }

      // Calculate impact relative to baseline
      const fpsImpact = baselineFPS > 0 ? ((baselineFPS - result.avgFPS) / baselineFPS) * 100 : 0;

      results.push({
        ...effect,
        ...result,
        fpsImpact: Math.round(fpsImpact * 10) / 10,
        recommendation: this.getEffectRecommendation(fpsImpact, result.jankRatio)
      });

      // Small delay between tests
      await new Promise(r => setTimeout(r, 200));
    }

    // Cleanup keyframes
    keyframeStyle.remove();

    // Generate combined recommendation
    const recommendation = this.generateEffectRecommendation(results);

    const debugInfo = this.getDebugInfo();
    const finalResults = {
      timestamp: Date.now(),
      baseline: results.find(r => r.id === 'baseline'),
      effects: results.filter(r => r.id !== 'baseline'),
      recommendation,
      device: {
        gpu: debugInfo.results?.proactive?.gpu,
        memory: debugInfo.results?.proactive?.memory,
        screenSize: debugInfo.screenSize,
        userAgent: debugInfo.userAgent
      }
    };

    this.detectionResults.effectBenchmark = finalResults;
    this.log('Effect benchmark complete', finalResults);

    if (completeCallback) {
      completeCallback(finalResults);
    }

    return finalResults;
  }

  /**
   * Test a single effect and measure FPS
   */
  testSingleEffect(effect) {
    return new Promise((resolve) => {
      const container = document.createElement('div');
      container.id = 'effect-benchmark-container';
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 999999;
        overflow: hidden;
        background: rgba(0,0,0,0.5);
      `;

      // Create 30 test elements with the effect
      for (let i = 0; i < 30; i++) {
        const box = document.createElement('div');
        box.style.cssText = `
          position: absolute;
          width: 120px;
          height: 80px;
          left: ${10 + (i % 6) * 15}%;
          top: ${10 + Math.floor(i / 6) * 18}%;
          ${effect.styles}
          animation: effectTestMove ${1.5 + Math.random()}s ease-in-out infinite;
        `;
        container.appendChild(box);
      }

      document.body.appendChild(container);

      // Measure FPS
      let samples = [];
      let lastTime = performance.now();
      let frameCount = 0;
      const testDuration = 120; // 2 seconds (~120 frames at 60fps)

      const measure = (time) => {
        const delta = time - lastTime;
        if (delta > 0) {
          samples.push(1000 / delta);
        }
        lastTime = time;
        frameCount++;

        if (frameCount < testDuration) {
          requestAnimationFrame(measure);
        } else {
          container.remove();

          const avgFPS = samples.length > 0
            ? samples.reduce((a, b) => a + b) / samples.length
            : 0;
          const minFPS = samples.length > 0 ? Math.min(...samples) : 0;
          const maxFPS = samples.length > 0 ? Math.max(...samples) : 0;
          const jankFrames = samples.filter(fps => fps < 24).length;

          resolve({
            avgFPS: Math.round(avgFPS * 10) / 10,
            minFPS: Math.round(minFPS * 10) / 10,
            maxFPS: Math.round(maxFPS * 10) / 10,
            jankRatio: Math.round((jankFrames / samples.length) * 1000) / 10,
            frames: samples.length
          });
        }
      };

      // Wait a frame for elements to render
      requestAnimationFrame(() => {
        requestAnimationFrame(measure);
      });
    });
  }

  /**
   * Get recommendation for a single effect based on impact
   */
  getEffectRecommendation(fpsImpact, jankRatio) {
    if (fpsImpact > 15 || jankRatio > 10) {
      return { status: 'disable', label: 'Disable', color: '#ef4444' };
    } else if (fpsImpact > 8 || jankRatio > 5) {
      return { status: 'reduce', label: 'Reduce', color: '#f59e0b' };
    } else {
      return { status: 'keep', label: 'Keep', color: '#22c55e' };
    }
  }

  /**
   * Generate combined recommendation based on all effect results
   */
  generateEffectRecommendation(results) {
    const effectSettings = {};

    for (const result of results) {
      if (result.id === 'baseline') continue;
      effectSettings[result.id] = result.recommendation.status;
    }

    // Determine overall profile
    const disableCount = Object.values(effectSettings).filter(s => s === 'disable').length;
    const reduceCount = Object.values(effectSettings).filter(s => s === 'reduce').length;

    let profile = 'normal';
    if (disableCount >= 3 || (disableCount >= 2 && reduceCount >= 2)) {
      profile = 'performance';
    } else if (disableCount >= 1 || reduceCount >= 2) {
      profile = 'reduced';
    }

    return {
      profile,
      effectSettings,
      summary: this.getProfileSummary(profile, effectSettings)
    };
  }

  /**
   * Get human-readable summary of the profile
   */
  getProfileSummary(profile, settings) {
    const lines = [];

    if (profile === 'performance') {
      lines.push('Your device struggles with multiple effects.');
      lines.push('Recommended: Enable Performance Mode for best experience.');
    } else if (profile === 'reduced') {
      lines.push('Your device handles most effects but some may cause slowdown.');
      lines.push('Recommended: Use Reduced Effects mode.');
    } else {
      lines.push('Your device handles visual effects well!');
      lines.push('Recommended: Keep all effects enabled.');
    }

    // Add specific recommendations
    const problemEffects = Object.entries(settings)
      .filter(([_, status]) => status === 'disable')
      .map(([effect, _]) => effect);

    if (problemEffects.length > 0) {
      lines.push(`Problem areas: ${problemEffects.join(', ')}`);
    }

    return lines.join(' ');
  }

  /**
   * Apply effect benchmark recommendations
   */
  applyEffectRecommendation(recommendation) {
    const { profile, effectSettings } = recommendation;

    // Apply the recommended profile
    if (profile === 'performance') {
      this.enable('effect-benchmark');
      this.savePreference('performance');
    } else if (profile === 'reduced') {
      this.enableReducedEffects();
      this.savePreference('reduced');
    } else {
      this.disable();
      this.savePreference('normal');
    }

    // Set individual effect data attributes for fine-grained control
    const html = document.documentElement;
    for (const [effect, status] of Object.entries(effectSettings)) {
      if (status === 'disable') {
        html.setAttribute(`data-effect-${effect}`, 'false');
      } else if (status === 'reduce') {
        html.setAttribute(`data-effect-${effect}`, 'reduced');
      } else {
        html.removeAttribute(`data-effect-${effect}`);
      }
    }

    this.log('Applied effect recommendations:', recommendation);
  }

  /**
   * Run verification test after applying recommendations
   */
  async verifyEffectSettings(callback) {
    // Run combined-full test to see if settings improved things
    const testEffect = {
      id: 'verification',
      name: 'Verification',
      styles: `
        background: rgba(42, 42, 64, 0.7);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-radius: 10px;
        box-shadow:
          0 4px 20px rgba(0,0,0,0.4),
          0 0 20px rgba(146, 64, 14, 0.1);
        animation: effectTestPulse 2s ease-in-out infinite;
      `
    };

    // Add keyframes if not present
    let keyframeStyle = document.getElementById('effect-benchmark-keyframes');
    if (!keyframeStyle) {
      keyframeStyle = document.createElement('style');
      keyframeStyle.id = 'effect-benchmark-keyframes';
      keyframeStyle.textContent = `
        @keyframes effectTestPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        @keyframes effectTestMove {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, 20px); }
        }
      `;
      document.head.appendChild(keyframeStyle);
    }

    const result = await this.testSingleEffect(testEffect);

    keyframeStyle.remove();

    const verification = {
      ...result,
      passed: result.avgFPS >= 45 && result.jankRatio < 10,
      message: result.avgFPS >= 45
        ? 'Settings verified! Performance is good.'
        : 'Performance still needs improvement. Consider enabling Performance Mode.'
    };

    this.detectionResults.effectVerification = verification;

    if (callback) callback(verification);
    return verification;
  }

  /**
   * Generate effect benchmark report for sharing
   */
  generateEffectReport() {
    const benchmark = this.detectionResults.effectBenchmark;
    if (!benchmark) {
      return { error: 'No benchmark data. Run the effect benchmark first.' };
    }

    return {
      version: '1.0',
      timestamp: benchmark.timestamp,
      device: benchmark.device,
      baseline: benchmark.baseline,
      effects: benchmark.effects.map(e => ({
        id: e.id,
        name: e.name,
        avgFPS: e.avgFPS,
        jankRatio: e.jankRatio,
        fpsImpact: e.fpsImpact,
        recommendation: e.recommendation.status
      })),
      recommendation: benchmark.recommendation,
      verification: this.detectionResults.effectVerification || null
    };
  }
}

// Initialize and expose globally
window.performanceMode = new PerformanceMode();

// Add CSS for any dynamic notifications
const performanceModeStyles = document.createElement('style');
performanceModeStyles.textContent = `
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  #performance-stress-test > div {
    will-change: transform, opacity;
  }
`;
document.head.appendChild(performanceModeStyles);
